import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';

import {
    buildSchemaGraph,
    generateOrganizationSchema,
    generateWebPageSchema,
    generateWebSiteSchema,
    renderStructuredData
} from 'utils/structuredData';
import { GEO_PAGE_DESCRIPTIONS } from 'static/geoPageInfo';

import PageFrame from 'ui-component/page-frame';
import PageTitle from 'ui-component/page-title';
import ServiceItem from './service-item';
import Timeline from './timeline';
import ContactMethod from './contact-method';
import ContentFrame from 'ui-component/content-frame';

const titlePhotoDesktop = `${process.env.REACT_APP_BASE_URL}/img/static/home-page/title-photo-3-2x.webp`;
const titlePhotoMobile = `${process.env.REACT_APP_BASE_URL}/img/static/home-page/title-photo-3-1x.webp`;
const officePhotoDesktop = `${process.env.REACT_APP_BASE_URL}/img/static/home-page/hotmob-office-2x.webp`;
const businessPhotoDesktop = `${process.env.REACT_APP_BASE_URL}/img/static/home-page/hotmob-business-2x.webp`;
const businessPhotoMobile = `${process.env.REACT_APP_BASE_URL}/img/static/home-page/hotmob-business-m-2x.webp`;

// ==============================|| HOME PAGE ||============================== //

const HomePage = () => {
    const location = useLocation();
    const theme = useTheme();

    // Client-only loaded components (to keep SSR safe)
    const [AnimatedBgComponent, setAnimatedBgComponent] = useState(null);
    const [CompanyGoogleMapComponent, setCompanyGoogleMapComponent] = useState(null);
    const [ContactUsFormComponent, setContactUsFormComponent] = useState(null);

    const pageTitleRef = useRef(null);
    const aboutUsRef = useRef(null);
    const contentRef = useRef(null);
    const contactFormRef = useRef(null);

    const [mediaServicesActive, isMediaServicesActive] = useState(false);
    const [dataServicesActive, isDataServicesActive] = useState(false);
    const isScrolling = useRef(false);

    const mdDownMedia = theme.breakpoints.down('md').replace(/^@media\s*/, '');

    const contentTitleSx = {
        background: 'linear-gradient(90deg, #E15C12 0%, #FCB336 44.5%);',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: 600,
        lineHeight: '100%',
        fontSize: { xs: '24px', md: '36px' },
        marginBottom: { xs: '24px', md: '40px' }
    };

    const isBelowMdViewport = () => typeof window !== 'undefined' && window.matchMedia(mdDownMedia).matches;

    useEffect(() => {
        const handleWheel = (event) => {
            if (isScrolling.current) {
                return;
            }

            const scrollY = window.scrollY;
            const contentTop = contentRef.current?.offsetTop || 0;
            const threshold = 50; // Proximity threshold for triggering scrolls

            // Scroll to content section when scrolling down near the top
            if (scrollY < threshold && event.deltaY > 0) {
                isScrolling.current = true;
                if (contentRef.current) {
                    contentRef.current.scrollIntoView({ behavior: 'smooth' });
                }
                setTimeout(() => {
                    isScrolling.current = false;
                }, 500); // Adjust based on smooth scroll duration
            }
            // Scroll to top when scrolling up near the content section
            else if (scrollY > contentTop - threshold && scrollY < contentTop + threshold && event.deltaY < 0) {
                isScrolling.current = true;
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => {
                    isScrolling.current = false;
                }, 500); // Adjust based on smooth scroll duration
            }
        };

        window.addEventListener('wheel', handleWheel, { passive: true });
        return () => {
            window.removeEventListener('wheel', handleWheel);
        };
    }, []);

    useEffect(() => {
        if (location.hash === '#contact-us' && contactFormRef.current) {
            isScrolling.current = true;
            // contactFormRef.current.scrollIntoView({ behavior: 'smooth' });
            window.scrollTo({
                top: contactFormRef.current.getBoundingClientRect().top + window.scrollY,
                behavior: 'smooth'
            });
            setTimeout(() => {
                isScrolling.current = false;
            }, 1000);
        } else if (location.hash === '#about-us' && aboutUsRef.current) {
            isScrolling.current = true;
            // aboutUsRef.current.scrollIntoView({ behavior: 'smooth' });
            window.scrollTo({
                top: aboutUsRef.current.getBoundingClientRect().top + window.scrollY,
                behavior: 'smooth'
            });
            setTimeout(() => {
                isScrolling.current = false;
            }, 1000);
        }
    }, [location.hash, contactFormRef.current, aboutUsRef.current]);

    const importContactComponents = async () => {
        const [MapModule, FormModule] = await Promise.all([import('./google-map'), import('./contact-us-form')]);
        setCompanyGoogleMapComponent(() => MapModule.default);
        setContactUsFormComponent(() => FormModule.default);
    };

    // Dynamically import heavy/browser-only child components on client mount
    useEffect(() => {
        let mounted = true;
        let idleId = null;
        let timeoutId = null;
        if (typeof window === 'undefined') {
            // keep return type consistent for ESLint
            return () => {
                mounted = false;
            };
        }

        const loadAnimatedBgWhenIdle = async () => {
            try {
                const Animated = await import('./animated-bg');
                if (!mounted) return;
                setAnimatedBgComponent(() => Animated.default);
            } catch (err) {
                // ignore import errors
            }
        };

        if ('requestIdleCallback' in window) {
            idleId = window.requestIdleCallback(loadAnimatedBgWhenIdle, { timeout: 3000 });
        } else {
            timeoutId = window.setTimeout(loadAnimatedBgWhenIdle, 2000);
        }

        return () => {
            mounted = false;
            if (idleId && 'cancelIdleCallback' in window) {
                window.cancelIdleCallback(idleId);
            }
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, []);

    // Load lower-fold contact components near viewport to improve TBT on initial view
    useEffect(() => {
        let mounted = true;
        if (typeof window === 'undefined') {
            return () => {
                mounted = false;
            };
        }

        const contactObserver = new IntersectionObserver(
            async (entries) => {
                if (!entries[0]?.isIntersecting || (CompanyGoogleMapComponent && ContactUsFormComponent)) return;
                try {
                    const [MapModule, FormModule] = await Promise.all([import('./google-map'), import('./contact-us-form')]);
                    if (!mounted) return;
                    setCompanyGoogleMapComponent(() => MapModule.default);
                    setContactUsFormComponent(() => FormModule.default);
                } catch (err) {
                    // ignore import errors
                } finally {
                    contactObserver.disconnect();
                }
            },
            { root: null, rootMargin: '600px 0px', threshold: 0.01 }
        );

        if (contactFormRef.current) contactObserver.observe(contactFormRef.current);

        return () => {
            mounted = false;
            contactObserver.disconnect();
        };
    }, [CompanyGoogleMapComponent, ContactUsFormComponent]);

    useEffect(() => {
        if (location.hash === '#contact-us') {
            importContactComponents().catch(() => {});
        }
    }, [location.hash]);

    // Generate structured data for SEO
    const baseUrl = process.env.REACT_APP_BASE_URL || 'https://hotmob.com';
    const pageUrl = `${baseUrl}`;
    const pageTitle = 'Hotmob - Data-Centric Media Company';
    const pageDescription = GEO_PAGE_DESCRIPTIONS.HOME;
    const pageImage = titlePhotoDesktop;
    // Single JSON-LD graph: Organization ↔ WebSite ↔ WebPage
    const structuredData = buildSchemaGraph([
        generateOrganizationSchema(),
        generateWebSiteSchema(),
        generateWebPageSchema({
            title: pageTitle,
            description: pageDescription,
            path: null,
            about: 'organization',
            image: {
                path: '/img/static/home-page/title-photo-3-2x.webp',
                width: 1377,
                height: 1255
            }
        })
    ]);

    return (
        <Box id="content" sx={{ width: '100%' }}>
            <Helmet>
                <html lang="en" />
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={pageUrl} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:image" content={pageImage} />
                <meta property="og:site_name" content="Hotmob" />
                <meta property="og:locale" content="en_US" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content={pageImage} />

                {/* Robots */}
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta name="googlebot" content="index, follow" />

                <link
                    rel="preload"
                    as="image"
                    href={titlePhotoMobile}
                    media={theme.breakpoints.down('sm').replace(/^@media\s*/, '')}
                    fetchpriority="high"
                />
                <link
                    rel="preload"
                    as="image"
                    href={titlePhotoDesktop}
                    media={theme.breakpoints.up('sm').replace(/^@media\s*/, '')}
                    fetchpriority="high"
                />

                {/* Structured Data */}
                {renderStructuredData(structuredData)}
            </Helmet>
            {AnimatedBgComponent ? <AnimatedBgComponent /> : null}
            <PageFrame>
                <Box ref={pageTitleRef} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <PageTitle
                        page="home-page"
                        columnOne="Data-Centric"
                        columnTwo="Media Company"
                        textBoxFlex="0 0 48%"
                        imageBoxFlex="0 0 50%"
                        columnOneColor="orange"
                        columnTwoColor="gray"
                        photo={titlePhotoDesktop}
                        photoMobile={titlePhotoMobile}
                        heroIntrinsicSize={{ width: 689, height: 628 }}
                        disableMediaReveal
                    >
                        <Box
                            sx={(t) => ({
                                minHeight: '153px',
                                display: 'flex',
                                justifyContent: 'space-around',
                                alignItems: 'center',
                                [t.breakpoints.down(900)]: {
                                    flexDirection: 'column'
                                }
                            })}
                        >
                            <ServiceItem
                                serviceName="Media Service"
                                description="Propel your digital<br />campaigns to new heights."
                                url="/media-service"
                                isActive={mediaServicesActive}
                                handleActiveUpdate={() => {
                                    if (isBelowMdViewport()) return;
                                    isMediaServicesActive(true);
                                    isDataServicesActive(false);
                                }}
                            />
                            <ServiceItem
                                serviceName="Data Service"
                                description="Data driven solutions.<br /> Take out the guesswork."
                                url="/data-service"
                                isActive={dataServicesActive}
                                handleActiveUpdate={() => {
                                    if (isBelowMdViewport()) return;
                                    isMediaServicesActive(false);
                                    isDataServicesActive(true);
                                }}
                            />
                        </Box>
                    </PageTitle>
                </Box>
                <ContentFrame innerRef={contentRef}>
                    <Grid
                        id="about-us"
                        ref={aboutUsRef}
                        item
                        xs={12}
                        sx={{ marginTop: '-80px', paddingLeft: '0 !important', paddingTop: '80px !important' }}
                    >
                        <Box sx={{ minHeight: { xs: '36px', lg: '24px' } }}>
                            <Typography sx={contentTitleSx}>Who We Are?</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ paddingLeft: '0 !important' }}>
                        <Box
                            sx={(t) => ({
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '80px',
                                marginBottom: '80px',
                                minHeight: '360px',
                                [t.breakpoints.down('md')]: {
                                    flexDirection: 'column-reverse',
                                    gap: '24px',
                                    minHeight: '430px'
                                },
                                [t.breakpoints.down('sm')]: {
                                    flexDirection: 'column-reverse',
                                    gap: '24px',
                                    minHeight: '570px'
                                }
                            })}
                        >
                            <Box>
                                <Typography
                                    sx={(t) => ({
                                        color: '#787878',
                                        fontSize: '18px',
                                        fontWeight: 400,
                                        lineHeight: '30px',
                                        [t.breakpoints.down('md')]: {
                                            fontSize: '16px',
                                            lineHeight: '25px'
                                        }
                                    })}
                                >
                                    Established in 2007, Hotmob is a leading data-centric media company based in Hong Kong. We empower
                                    brands and marketers across APAC and the Greater Bay Area (GBA) with two core offerings: media services
                                    and data services. Our media services cover programmatic advertising, premium media placement, campaign
                                    management, China media solutions, and AI-driven creative. Our data services unify internal and external
                                    data to deliver market intelligence, consumer insights, data enrichment, and ETL-powered data
                                    engineering — helping businesses execute seamless digital campaigns and make strategic, data-driven
                                    decisions.
                                </Typography>
                            </Box>
                            <img
                                alt="Hotmob Office"
                                loading="lazy"
                                width="404"
                                height="268"
                                src={officePhotoDesktop}
                                style={{ width: '100%', height: 'auto', maxWidth: '400px' }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ paddingLeft: '0 !important' }}>
                        <Box sx={(t) => ({ marginBottom: '80px', [t.breakpoints.down('md')]: { minHeight: '400px' } })}>
                            <Box sx={{ minHeight: { xs: '36px', lg: '24px' } }}>
                                <Typography sx={contentTitleSx}>With Business In</Typography>
                            </Box>
                            <Box sx={{ minHeight: { xs: '389px', lg: '335px' } }}>
                                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                    <img
                                        alt="Scope of Hotmob Business"
                                        loading="lazy"
                                        width="353"
                                        height="353"
                                        src={businessPhotoMobile}
                                        style={{ width: '100%', height: 'auto', maxWidth: '500px', display: 'block', margin: '0 auto' }}
                                    />
                                </Box>
                                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <img
                                        alt="Scope of Hotmob Business"
                                        loading="lazy"
                                        width="1303"
                                        height="389"
                                        src={businessPhotoDesktop}
                                        style={{ width: '100%', height: 'auto', maxWidth: '1303px', display: 'block', margin: '0 auto' }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sx={{ paddingLeft: '0 !important' }}>
                        {/* Timeline should stay server-rendered for SEO/content completeness */}
                        <Timeline />
                    </Grid>
                    <Grid id="contact-us" container item spacing={2} ref={contactFormRef}>
                        <Grid item xs={12} sx={{ paddingLeft: '0 !important' }}>
                            <Box sx={{ minHeight: { xs: '36px', lg: '24px' } }}>
                                <Typography sx={contentTitleSx}>Contact Us</Typography>
                            </Box>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={6}
                            lg={6}
                            sx={(t) => ({
                                paddingLeft: '0 !important',
                                paddingRight: '16px',
                                [t.breakpoints.down('md')]: { paddingRight: '0' }
                            })}
                        >
                            {CompanyGoogleMapComponent ? (
                                <CompanyGoogleMapComponent />
                            ) : (
                                <Box sx={(t) => ({ width: '100%', height: '400px', [t.breakpoints.down('md')]: { height: '350px' } })} />
                            )}
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={6}
                            lg={6}
                            sx={(t) => ({ [t.breakpoints.down('md')]: { paddingLeft: '0 !important' } })}
                        >
                            <ContactMethod />
                            {ContactUsFormComponent ? <ContactUsFormComponent /> : <Box sx={{ minHeight: { xs: '560px', md: '620px' } }} />}
                        </Grid>
                    </Grid>
                </ContentFrame>
            </PageFrame>
        </Box>
    );
};

export default HomePage;
